import { coachSide, type ProjectState } from "@livcg/schema";
import { useState } from "react";
import { sendRaw } from "../../shared/sendRaw.js";
import { useHotkeys } from "../../shared/useHotkeys.js";
import { cn, useToast } from "../../ui/index.js";
import { allOffSurfaces } from "../allOff.js";
import { Drawer } from "../control-room/Drawer.js";
import { OverlayBoardView } from "../control-room/OverlayBoard.js";
import { noDataTitle, overlayDataFor, rosterModeHasData } from "../control-room/overlayData.js";
import { useOverlayExtras } from "../control-room/useOverlayExtras.js";
import { useOverlaySwitch } from "../control-room/useOverlaySwitch.js";
import { useQuickEventChain } from "../control-room/useQuickEventChain.js";
import { overlayModeLabel } from "../overlayModes.js";
import { byNumber } from "../rosterOrder.js";
import { useMatchDetail } from "../useMatchDetail.js";
import {
  bannerLabel,
  type Cue,
  type CuePlayer,
  cardCue,
  goalCue,
  hideCue,
  onPitch,
  type Side,
  type SubPair,
  showCue,
  subsCue,
  yellowType,
} from "./cue.js";
import { Monitor } from "./Monitors.js";
import { OverlayPad, type TileState } from "./OverlayPad.js";
import { type PadPlayer, PlayerPad } from "./PlayerPad.js";
import { TopBar } from "./TopBar.js";

/** Efir — preview → take. Every on-air action is cued into PREVIEW (the real
 *  graphic) and goes out only on EFIRGA, through the same hooks as the other
 *  views. Clock, phase and ALL OFF act directly. */
export function EfirView({ slug, state }: { slug: string; state: ProjectState }) {
  const { toast } = useToast();
  const extrasCtx = useOverlayExtras(state);
  const sw = useOverlaySwitch(slug, state, extrasCtx.extras);
  const chain = useQuickEventChain(slug, state);
  const md = useMatchDetail(state);
  const pv = state.preview ?? null;

  const [side, setSide] = useState<Side>("home");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingOut, setPendingOut] = useState<CuePlayer | null>(null);
  const [pairs, setPairs] = useState<SubPair[]>([]);
  const [moreOpen, setMoreOpen] = useState(false);

  const cue = (c: Cue) => void sendRaw("preview.set", { slug, preview: c });
  const resetSubs = () => {
    setPendingOut(null);
    setPairs([]);
  };
  const clear = () => {
    resetSubs();
    if (pv) void sendRaw("preview.clear", { slug });
  };
  const allOff = () => allOffSurfaces(slug, state);

  // --- players -------------------------------------------------------------
  const playersOf = (s: Side): PadPlayer[] => {
    const block = md.detail?.[s];
    if (!block) return [];
    const roster = block.roster ?? [];
    const starters = roster.filter((r) => r.isStarter);
    const pitch = onPitch(
      starters.map((r) => r.playerId),
      md.sortedEvents,
    );
    const list: CuePlayer[] =
      roster.length > 0
        ? [...starters, ...roster.filter((r) => !r.isStarter)]
            .sort(
              (a, b) =>
                Number(pitch.has(b.playerId)) - Number(pitch.has(a.playerId)) ||
                byNumber(a, b),
            )
            .map((r) => ({
              id: r.playerId,
              firstName: r.firstName,
              lastName: r.lastName,
              number: r.number,
            }))
        : [...block.players].sort(byNumber);
    const card = (id: string) => state.match.playerCards?.[id];
    const players: PadPlayer[] = list.map((pl) => ({
      ...pl,
      onPitch: roster.length === 0 || pitch.has(pl.id),
      sentOff: !!card(pl.id)?.sentOff,
      yellows: card(pl.id)?.yellows ?? 0,
      coach: false,
    }));
    const coach = md.coach(s);
    return [
      ...players,
      {
        ...coach,
        onPitch: false,
        sentOff: !!card(coach.id)?.sentOff,
        yellows: card(coach.id)?.yellows ?? 0,
        coach: true,
      },
    ];
  };
  const players = playersOf(side);
  const selected = players.find((p) => p.id === selectedId) ?? null;

  const changeSide = (s: Side) => {
    setSide(s);
    setSelectedId(null);
    resetSubs();
  };

  const select = (id: string) => {
    const pl = players.find((p) => p.id === id);
    if (!pl) return;
    if (pendingOut) {
      // Second pick of a substitution: the player coming on.
      const next = [...pairs, { out: pendingOut, in: pl }];
      setPairs(next);
      setPendingOut(null);
      setSelectedId(null);
      cue(subsCue(side, next));
      return;
    }
    setSelectedId(id === selectedId ? null : id);
  };

  const cueGoal = (type: "goal" | "penalty_goal" | "own_goal") => {
    if (!selected || selected.coach || selected.sentOff) return;
    resetSubs();
    cue(goalCue(type, side, selected));
  };
  const cueYellow = () => {
    if (!selected || selected.sentOff) return;
    resetSubs();
    cue(cardCue(yellowType(selected.yellows), side, selected, selected.coach));
  };
  const cueRed = () => {
    if (!selected || selected.sentOff) return;
    resetSubs();
    cue(cardCue("red_card", side, selected, selected.coach));
  };
  const startSub = () => {
    if (!selected || selected.coach || !selected.onPitch) return;
    // A cue for another side or kind is replaced by this substitution.
    if (pv?.action.kind !== "subs" || pv.action.side !== side) setPairs([]);
    setPendingOut(selected);
    setSelectedId(null);
  };

  // --- overlays ------------------------------------------------------------
  const cuedMode =
    pv && (pv.action.kind === "show" || pv.action.kind === "hide") ? pv.action.mode : null;
  const tileState = (mode: string): TileState => {
    const onAir = sw.isActive(mode);
    const noData = !onAir && !rosterModeHasData(mode, state, extrasCtx.extras);
    return {
      onAir,
      cued: cuedMode === mode,
      disabled: noData,
      ...(noData ? { title: noDataTitle(mode) } : {}),
    };
  };
  const cueTile = (mode: string) => {
    resetSubs();
    if (cuedMode === mode) return clear();
    const label = overlayModeLabel(mode);
    if (sw.isActive(mode)) cue(hideCue(mode, sw.offTarget(mode), `Yashirish · ${label}`));
    else cue(showCue(mode, overlayDataFor(mode, state, extrasCtx.extras), label));
  };

  // --- take ----------------------------------------------------------------
  const take = () => {
    if (!pv) return;
    if (sw.busyMode) {
      toast({ tone: "error", title: "Biroz kuting", description: "Oldingi o'tish hali tugamadi" });
      return;
    }
    const a = pv.action;
    if (a.kind === "show") {
      if (!sw.isActive(a.mode)) sw.toggle(a.mode);
    } else if (a.kind === "hide") {
      if (sw.isActive(a.mode)) sw.toggle(a.mode);
    } else {
      const block = md.detail?.[a.side];
      if (!block) {
        toast({
          tone: "error",
          title: "Tarkib yuklanmagan",
          description: "Efirga uzatib bo'lmadi",
        });
        return;
      }
      const all = playersOf(a.side);
      const find = (id: string) => all.find((p) => p.id === id);
      if (a.kind === "event") {
        const pl = coachSide(a.playerId) ? md.coach(a.side) : find(a.playerId);
        if (!pl) return;
        // The same chain still on air would be toggled off by fire().
        if (chain.activeChain !== `${a.type}:${a.side}:${a.playerId}`) {
          chain.fire(a.type, a.side, pl, block.team);
        }
      } else {
        const list = a.pairs
          .map(({ outId, inId }) => ({ out: find(outId), in: find(inId) }))
          .filter((x): x is { out: PadPlayer; in: PadPlayer } => !!x.out && !!x.in)
          .map((x) => ({
            outId: x.out.id,
            outName: x.out.lastName || x.out.firstName,
            outNumber: x.out.number,
            inId: x.in.id,
            inName: x.in.lastName || x.in.firstName,
            inNumber: x.in.number,
          }));
        chain.fireSubGroup(a.side, list, block.team);
      }
    }
    setSelectedId(null);
    clear();
  };

  useHotkeys({
    enter: () => take(),
    esc: () => {
      if (!moreOpen) clear();
    },
    space: () => void sendRaw("clock.toggle", { slug }),
    g: () => cueGoal("goal"),
    y: () => cueYellow(),
    r: () => cueRed(),
    s: () => startSub(),
    "shift+esc": () => allOff(),
  });

  const teamNames = {
    home: state.teams.home?.shortName || "Uy",
    away: state.teams.away?.shortName || "Mehmon",
  };
  const programCaption = sw.current
    ? (bannerLabel(sw.current) ?? overlayModeLabel(sw.current))
    : "Efir bo'sh";

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-hidden p-3">
      <TopBar slug={slug} state={state} onAllOff={allOff} />

      <div className="flex h-[clamp(200px,36vh,430px)] shrink-0 items-stretch justify-center gap-3">
        <Monitor
          slug={slug}
          kind="preview"
          caption={
            pv ? (
              <span className="text-ink">Tayyor: {pv.label}</span>
            ) : (
              <span className="text-ink-faint">Tayyor amal yo'q — efirdagining o'zi</span>
            )
          }
        />
        <div className="flex w-[170px] shrink-0 flex-col gap-2 self-center">
          <button
            type="button"
            onClick={take}
            disabled={!pv}
            className={cn(
              "flex h-28 flex-col items-center justify-center rounded-xl text-[24px] font-black uppercase tracking-[0.08em] transition-colors",
              pv
                ? "bg-live text-white shadow-lg hover:brightness-110"
                : "cursor-not-allowed bg-surface-elevated text-ink-faint",
            )}
          >
            Efirga
            <span className="text-[11px] font-semibold opacity-80">Enter</span>
          </button>
          <button
            type="button"
            onClick={clear}
            disabled={!pv && !pendingOut}
            className="h-12 rounded-xl border border-border bg-surface-elevated text-[15px] font-bold text-ink disabled:opacity-35"
          >
            Bekor <span className="text-[11px] font-semibold text-ink-muted">Esc</span>
          </button>
        </div>
        <Monitor slug={slug} kind="program" caption={<span>Efirda: {programCaption}</span>} />
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-[1.35fr_1fr] gap-3">
        <PlayerPad
          side={side}
          onSide={changeSide}
          teamNames={teamNames}
          players={players}
          selectedId={selectedId}
          onSelect={select}
          pendingOut={pendingOut}
          pairs={pairs}
          onGoal={cueGoal}
          onYellow={cueYellow}
          onRed={cueRed}
          onSub={startSub}
        />
        <OverlayPad tileState={tileState} onTile={cueTile} onMore={() => setMoreOpen(true)} />
      </div>

      <Drawer
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        title="Barcha overlay'lar — to'g'ridan-to'g'ri efirga"
        width="max-w-4xl"
      >
        <OverlayBoardView
          slug={slug}
          state={state}
          onAllOff={allOff}
          showClassicOnly
          extrasCtx={extrasCtx}
          sw={sw}
        />
      </Drawer>
    </div>
  );
}
